import { useEffect, useState } from 'react'
import { getWorkCategories } from '../../api/workApi'
import type { WorkCategoryInfo, WorkSubcategoryInfo, WorkWizardState } from '../../types/work'
import WorkCategoryCard from './WorkCategoryCard'
import WorkCategoryDetail from './WorkCategoryDetail'

interface WorkWizardStep1Props {
  state: WorkWizardState
  onChange: (patch: Partial<WorkWizardState>) => void
  onNext: () => void
}

export default function WorkWizardStep1({ state, onChange, onNext }: WorkWizardStep1Props) {
  const [categories, setCategories] = useState<WorkCategoryInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const selectedCategory = categories.find(c => c.id === state.category) ?? null

  useEffect(() => {
    getWorkCategories()
      .then(setCategories)
      .catch(() => setError('Failed to load categories'))
      .finally(() => setLoading(false))
  }, [])

  function handleCategorySelect(cat: WorkCategoryInfo) {
    onChange({ category: cat.id, subcategory: undefined })
  }

  function handleSubcategorySelect(sub: WorkSubcategoryInfo) {
    onChange({ subcategory: sub.id })
  }

  if (loading) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            style={{
              height: '100px',
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.05)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        ))}
        <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ color: 'var(--danger, #ef4444)', fontSize: '13px', padding: '20px', textAlign: 'center' }}>
        {error}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Split layout: category list left, detail right */}
      <div style={{ display: 'grid', gridTemplateColumns: selectedCategory ? '220px 1fr' : '1fr', gap: '16px' }}>
        {/* Category list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {categories.map(cat => (
            <WorkCategoryCard
              key={cat.id}
              category={cat}
              selected={state.category === cat.id}
              onSelect={() => handleCategorySelect(cat)}
            />
          ))}
        </div>

        {/* Detail panel */}
        {selectedCategory && (
          <WorkCategoryDetail
            category={selectedCategory}
            onSubcategorySelect={handleSubcategorySelect}
            selectedSubcategory={state.subcategory}
          />
        )}
      </div>

      {/* Next button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={onNext}
          disabled={!state.category}
          style={{
            padding: '10px 24px',
            borderRadius: '10px',
            background: state.category ? 'var(--primary, #6366f1)' : 'var(--border-color)',
            color: state.category ? '#fff' : 'var(--text-muted, #6b7280)',
            border: 'none',
            fontSize: '14px',
            fontWeight: 600,
            cursor: state.category ? 'pointer' : 'not-allowed',
            transition: 'background 0.15s',
          }}
        >
          Next →
        </button>
      </div>
    </div>
  )
}
